import { LightningLanderThemeSettings } from "features/lightningLanders/types";
import { landerDefaultTheme } from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { Editor, Selector } from "grapesjs";
import { getRootDomain, getScheme } from "features/subdomains/utils";

interface MediaQuery {
  params: string;
  style: Record<string, string>;
}

interface GridSelector {
  name: string;
  style: Record<string, string>;
  mediaQueries?: MediaQuery[];
}

/**
 * Safely retrieves the canvas document from the GrapesJS editor.
 * @param editor GrapesJS editor instance
 * @returns Canvas document or null if unavailable
 */
export const getCanvasDocument = (editor: any): Document | null => {
  try {
    return editor?.Canvas?.getDocument() || null;
  } catch {
    return null;
  }
};

/**
 * Adds or updates a script tag in the canvas document.
 * @param canvasDoc Canvas document
 * @param scriptAttr Attribute to identify the script tag
 * @param scriptContent JavaScript content to add or update
 */
export const addOrUpdateScriptTag = (
  canvasDoc: Document,
  scriptAttr: string,
  scriptContent: string
) => {
  try {
  const existingScript = canvasDoc.querySelector(`script[${scriptAttr}]`);
  if (existingScript) {
    existingScript.textContent = scriptContent;
  } else {
    const script = canvasDoc.createElement("script");
    script.setAttribute(scriptAttr, "true");
    script.textContent = scriptContent;
    canvasDoc.body.appendChild(script);
  }
  } catch (error) {
    console.error('Error adding or updating script tag:', error);
  }
};

/**
 * Forces a re-render by incrementing a numeric state.
 * @param setTrigger State updater function for a numeric state
 */
export const forceUpdate = (
  setTrigger: React.Dispatch<React.SetStateAction<number>>
) => {
  setTrigger((prev) => prev + 1);
};

/**
 * Generates CSS variables and base styles from a theme configuration.
 * @param theme Theme configuration object
 * @returns CSS string containing variables and base styles
 */
export const generateThemeStyles = (
  theme: LightningLanderThemeSettings = landerDefaultTheme
): string => {
  const cssVars = `
    :root {
      --font-family: ${theme.bodyFontFamily || "Arial, sans-serif"};
      --primary-color: ${theme.primaryColor || "#000000"};
      --secondary-color: ${theme.secondaryColor || "#666666"};
      --text-color: ${theme.textColor || "#000000"};
      --font-size-h1: ${theme.fontSizeH1 || "2rem"};
      --font-size-h2: ${theme.fontSizeH2 || "1.5rem"};
      --font-size-body: ${theme.fontSizeBody || "1rem"};
      --container-width: ${theme.containerWidth || "1200px"};
    }
    h1 { font-size: var(--font-size-h1); color: var(--text-color); }
    h2 { font-size: var(--font-size-h2); color: var(--text-color); }
    body { color: var(--text-color); font-size: var(--font-size-body); font-family: var(--font-family); }
    a { color: var(--primary-color); }
  `;

  return cssVars;
};

export const colorVariableToCustomTheme = (variable: string): string | null => {
  const customTheme = landerDefaultTheme;
  if (variable.includes('--primary-color')) {
    return customTheme.primaryColor;
  } else if (variable.includes('--secondary-color')) {
    return customTheme.secondaryColor;
  } else if (variable.includes('--text-color')) {
    return customTheme.textColor;
  }
  return null;
}


export const addGridStyles = (editor: Editor) => {
  const selectors: GridSelector[] = [
    {
      name: 'inc-grid',
      style: { display: 'flex', 'flex-direction': 'row', gap: '20px', width: '100%' },
      mediaQueries: [
        {
          params: '(max-width: 768px)',
          style: { 'flex-direction': 'column' }
        }
      ]
    },
    ...Array.from({ length: 12 }, (_, i) => ({
      name: `inc-md-col-${i + 1}`,
      style: { width: `${((i + 1) / 12 * 100).toFixed(2)}%`, 'min-height': '50px' }
    })),
    {
      name: 'inc-sm-col-12',
      style: {},
      mediaQueries: [
        {
          params: '(max-width: 768px)',
          style: { width: '100%' }
        }
      ]
    }
  ];

  selectors.forEach(sel => {
    if (editor.SelectorManager.get(sel.name)) {
      return;
    }
    const selector = editor.SelectorManager.add(sel.name, { type: 1 }) as Selector;
    selector.set('active', true);
    selector.setActive(true);

    // Add the main style rule
    editor.Css.setRule(selector, sel.style);

    // Add media query rules if defined
    if (sel.mediaQueries) {
      sel.mediaQueries.forEach(mediaQuery => {
        editor.Css.setRule(selector, mediaQuery.style, {
          atRuleType: 'media',
          atRuleParams: mediaQuery.params
        });
      });
    }
  });
};

/**
 * Generates a URL-friendly path slug from a name.
 * Converts to lowercase, replaces spaces with hyphens, and removes special characters.
 * @param name The name to convert to a path
 * @returns URL-friendly slug (without leading slash)
 */
export const generatePathFromName = (name: string): string => {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  return slug ? slug : '';
};

/**
 * Normalizes a landing path to ensure it starts with "/" and doesn't end with "/".
 * Used for analytics and display purposes.
 * @param path The path to normalize
 * @returns Normalized path starting with "/"
 */
export const normalizeLandingPath = (path: string | undefined): string => {
  if (!path) {
    return '/';
  }
  // Remove trailing slash if present
  const trimmed = path.slice(-1) === '/' ? path.slice(0, -1) : path;
  // Ensure it starts with "/"
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

/**
 * Builds a full URL from subdomain and path.
 * @param subdomain The subdomain object with domain and subdomain properties
 * @param path The landing path (without leading slash)
 * @param includeTrailingSlash Whether to include trailing slash (default: true)
 * @returns Full URL or "[not-set]" if subdomain/path not available
 */
export const buildLandingUrl = (
  subdomain?: { domain?: string; subdomain?: string } | null,
  path?: string | null,
  includeTrailingSlash: boolean = true
): string => {
  if (!subdomain?.domain || !path) {
    return '[not-set]';
  }

  const scheme = getScheme(subdomain.domain);
  const rootDomain = getRootDomain(subdomain.domain);
  const subdomainName = subdomain.subdomain || '';

  // Ensure path starts with "/" for the URL
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
  const trailing = includeTrailingSlash ? '/' : '';
  return `${scheme}//${subdomainName}.${rootDomain}${pathWithSlash}${trailing}`;
};

/**
 * Builds a preview URL for a landing page from the name and subdomain.
 * @param name The landing page name
 * @param subdomain The subdomain object with domain and subdomain properties
 * @returns Full preview URL or empty string if invalid
 */
export const buildPreviewUrl = (
  name: string,
  subdomain?: { domain?: string; subdomain?: string } | null
): string => {
  if (!name || name.trim().length < 3 || !subdomain?.domain) {
    return '';
  }

  const generatedPath = generatePathFromName(name);
  if (!generatedPath) {
    return '';
  }

  // Use buildLandingUrl for consistency
  return buildLandingUrl(subdomain, generatedPath, false);
};  